import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChakraProvider, extendTheme } from '@chakra-ui/react';
import { Outlet, useParams } from 'react-router-dom';
import { withI18NextTranslate } from 'sp-ui';
import { LoadingIndicator } from 'ts/common/components';
import ClientPageHeading from 'ts/common/components/ClientPageHeading';
import ClientPageLayout from 'ts/common/components/ClientPageLayout';
import { getTheme } from 'ts/common/components/gallery';
import { type IFetchHookResponse, useTranslate } from 'ts/common/hooks';
import { useBootstrapData, withBootstrapData } from 'client_react/bootstrap';
import { FetchMethod, useClientApiFetch } from 'client_react/bootstrap';

interface QuestionnaireError {
    detail?: string;
    status: number;
}

const isQuestionnaireError = (response: unknown): response is QuestionnaireError => {
    return (
        response !== null &&
        typeof response === 'object' &&
        'status' in response &&
        typeof (response as QuestionnaireError).status === 'number' &&
        (response as QuestionnaireError).status >= 400
    );
};

export type QuestionnaireContext = {
    fetchQuestionnaire: (
        email?: string
    ) => Promise<
        IFetchHookResponse<
            SpApi.Client.IQuestionnaireMinimal | SpApi.Client.IQuestionnaire | QuestionnaireError
        >
    >;
    loading: boolean;
    error: QuestionnaireError | null;
    questionnaire: SpApi.Client.IQuestionnaireMinimal | SpApi.Client.IQuestionnaire;
};

const QuestionnaireAppContent: React.FC = () => {
    const t = useTranslate('clientQuestionnaire');

    const { questionnaireId } = useParams<{ questionnaireId: string }>();
    const [error, setError] = useState<QuestionnaireError | null>(null);
    const [questionnaire, setQuestionnaire] = useState<
        SpApi.Client.IQuestionnaireMinimal | SpApi.Client.IQuestionnaire
    >();

    const { loading, performFetch } = useClientApiFetch<
        SpApi.Client.IQuestionnaire | QuestionnaireError
    >(`questionnaire/${questionnaireId}`, {
        defer: true,
        method: FetchMethod.GET
    });

    const fetchQuestionnaire = useCallback(
        async (email?: Nullable<string>) => {
            const response = await performFetch({
                search: email ? { email } : undefined
            });

            if (response && !isQuestionnaireError(response)) {
                setQuestionnaire(response);
                setError(null);

                if (email) {
                    sessionStorage.setItem(`questionnaire-email-${questionnaireId}`, email);
                }
            } else {
                setError(response ?? null);
            }

            return response;
        },
        [performFetch, questionnaireId]
    );

    useEffect(() => {
        const email = sessionStorage.getItem(`questionnaire-email-${questionnaireId}`);

        (async () => {
            const response = await fetchQuestionnaire(email);

            if (response?.status === 403) {
                sessionStorage.removeItem(`questionnaire-email-${questionnaireId}`);
                await fetchQuestionnaire();
            }
        })();
    }, [fetchQuestionnaire, questionnaireId]);

    const theme = useMemo(
        () => (questionnaire ? extendTheme(getTheme(questionnaire.brandTheme)) : extendTheme()),
        [questionnaire]
    );

    if (loading && !questionnaire) {
        return (
            <ChakraProvider resetCSS={false} theme={theme}>
                <LoadingIndicator />
            </ChakraProvider>
        );
    }

    if (!questionnaire && error?.status === 404) {
        return (
            <div>
                <h1>{t('questionnaireNotFound')}</h1>
                <p>{t('questionnaireNotFoundMessage')}</p>
            </div>
        );
    }

    if (!questionnaire) {
        return (
            <div>
                <h1>{t('errorLoadingQuestionnaire')}</h1>
                <p>{t('unableToLoadQuestionnaire')}</p>
            </div>
        );
    }

    const context: QuestionnaireContext = {
        fetchQuestionnaire,
        loading,
        error,
        questionnaire
    };

    return (
        <ChakraProvider resetCSS={false} theme={theme}>
            <ClientPageLayout
                banner={<ClientPageHeading>{questionnaire.name}</ClientPageHeading>}
                headingText={questionnaire.brandName}
                showLegalLinks
            >
                <Outlet context={context} />
            </ClientPageLayout>
        </ChakraProvider>
    );
};

const QuestionnaireApp: React.FC = withBootstrapData(() => {
    const { translations } = useBootstrapData();

    const PageWithTranslations = useMemo(
        () => withI18NextTranslate(QuestionnaireAppContent, translations),
        [translations]
    );

    return <PageWithTranslations />;
});

export default QuestionnaireApp;
